import { IconDragIndicator } from "@/assets/icons";
import { Switch } from "@/components/elements/Switch";
import { divider, text } from "@/theme/colors";
import { Box, Stack, styled, Typography } from "@mui/material";
import { FC } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";

type ColumnItem = {
  key: string;
  label: string;
  isAlwaysShowing?: boolean;
};

type Props = {
  columns: ColumnItem[];
  visibleColumns: Record<string, boolean>;
  onToggleColumn: (key: string) => void;
  onDragEnd: (result: DropResult) => void;
};

export const OrganizationDisplaySettingsTabContent: FC<Props> = ({ columns, visibleColumns, onToggleColumn, onDragEnd }) => {
  const handleHideColumn = (item: ColumnItem) => {
    onToggleColumn(item.key);
  };

  return (
    <Box px={3} pb={2}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="column-display-items">
          {(provided) => (
            <Stack gap={0} ref={provided.innerRef} {...provided.droppableProps}>
              {columns.map((item, index) => (
                <Draggable key={item.key} draggableId={item.key} index={index}>
                  {(provided, snapshot) => (
                    <Stack
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      py="4px"
                      sx={{
                        backgroundColor: snapshot.isDragging ? "#f5f5f5" : "transparent",
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <NumberCircle>{index + 1}</NumberCircle>
                        <IconDragIndicator />
                        <Typography variant="body14" color={text.primary}>
                          {item.label}
                        </Typography>
                      </Stack>
                      <Switch
                        disabled={item?.isAlwaysShowing}
                        size="medium"
                        checked={visibleColumns[item.key] ?? true}
                        onChange={() => handleHideColumn(item)}
                      />
                    </Stack>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

const NumberCircle = styled(Box)`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${divider.high};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: ${text.secondary};
`;
